
import React from 'react';

const CLUSTER_TRANSITION_DURATION = 500;
const THRESHOLD_DISTANCE = 1000;
const SELECTED_FOLLOW_EPSILON = 0.000001;

const getFollowCenterLatLng = (map, position, offsetY = 0, zoom = map.getZoom()) => {
  if (!map || !position) return null;

  const latLng = L.latLng(position[0], position[1]);
  const markerPoint = map.project(latLng, zoom);
  const nextCenterPoint = markerPoint.subtract([0, offsetY]);

  return map.unproject(nextCenterPoint, zoom);
};

const AnimationManager = ({
  listVehicleTracking,
  mapRef,
  clusters,
  supercluster,
  vehicleSelected,
  vehiclePositionsRef,
  lastKnownPositionsRef,
  clusterPositionsRef,
  markerRef,
  vehicleIconsRef,
  animationRef,
  lastUpdateRef,
  isPageVisible,
  selectedVehicleFollowOffsetY = 0
}) => {
  React.useEffect(() => {
    if (!listVehicleTracking?.length || !mapRef) return;

    const bounds = mapRef.getBounds();
    const visibleMarkers = {};
    listVehicleTracking.forEach(v => {
      const lat = Number(v.lat) || 0;
      const lng = Number(v.lng) || 0;
      visibleMarkers[v.device_id] = bounds.contains([lat, lng]);
    });

    clusterPositionsRef.current = {};
    clusters.forEach(cluster => {
      if (!cluster.properties.cluster) return;
      const pos = [cluster.geometry.coordinates[1], cluster.geometry.coordinates[0]];
      const leaves = supercluster.getLeaves(cluster.properties.cluster_id, Infinity);
      leaves.forEach(leaf => {
        const id = leaf.properties.vehicle.device_id;
        if (vehiclePositionsRef.current[id]) {
          lastKnownPositionsRef.current[id] = vehiclePositionsRef.current[id];
        }
        clusterPositionsRef.current[id] = pos;
      });
    });

    listVehicleTracking.forEach(vehicle => {
      if (vehicle.debt) return;

      const deviceId = vehicle.device_id;
      const newPos = [Number(vehicle.lat) || 0, Number(vehicle.lng) || 0];
      const isInViewport = visibleMarkers[deviceId];
      const isSelected = vehicleSelected.device_id === deviceId;

      if (!isInViewport && !isSelected) {
        vehiclePositionsRef.current[deviceId] = newPos;
        return;
      }

      const wasInCluster = !!clusterPositionsRef.current[deviceId];
      const oldPos = vehiclePositionsRef.current[deviceId] || newPos;
      const lastKnown = lastKnownPositionsRef.current[deviceId] || oldPos;
      const isInCluster = !!clusterPositionsRef.current[deviceId];

      if (isInCluster) {
        vehiclePositionsRef.current[deviceId] = newPos;
        return;
      }

      const samePos = oldPos[0] === newPos[0] && oldPos[1] === newPos[1];
      if (samePos && !wasInCluster && !isInCluster) {
        vehiclePositionsRef.current[deviceId] = newPos;
        markerRef.current[deviceId]?.setLatLng(newPos);
        return;
      }

      const sameAsLast = lastKnown[0] === newPos[0] && lastKnown[1] === newPos[1];
      if (wasInCluster && !isInCluster && sameAsLast) {
        vehiclePositionsRef.current[deviceId] = newPos;
        markerRef.current[deviceId]?.setLatLng(newPos);
        return;
      }

      const newIcon = window.handleDisplayIcon?.(vehicle);
      if (newIcon && markerRef.current[deviceId]) {
        const current = vehicleIconsRef.current[deviceId];
        if (!current || current.options.iconUrl !== newIcon.options.iconUrl) {
          markerRef.current[deviceId].setIcon(newIcon);
          vehicleIconsRef.current[deviceId] = newIcon;
        }
      }

      const oldLatLng = L.latLng(oldPos[0], oldPos[1]);
      const newLatLng = L.latLng(newPos[0], newPos[1]);
      const distance = oldLatLng.distanceTo(newLatLng);

      if (!isPageVisible || distance > THRESHOLD_DISTANCE) {
        vehiclePositionsRef.current[deviceId] = newPos;
        if ((isInViewport || isSelected)) {
          markerRef.current[deviceId]?.setLatLng(newPos);
        }
        if (isSelected) {
          const center = mapRef.getCenter();
          const followCenter = getFollowCenterLatLng(
            mapRef,
            newPos,
            selectedVehicleFollowOffsetY
          );
          if (
            followCenter &&
            (
              Math.abs(center.lat - followCenter.lat) > SELECTED_FOLLOW_EPSILON ||
              Math.abs(center.lng - followCenter.lng) > SELECTED_FOLLOW_EPSILON
            )
          ) {
            mapRef.setView(followCenter, mapRef.getZoom(), { animate: false });
          }
        }
      } else {
        const startPos = wasInCluster ? lastKnown : oldPos;
        const duration = wasInCluster && !isInCluster ? CLUSTER_TRANSITION_DURATION : 10000;

        if (animationRef.current[deviceId]) {
          cancelAnimationFrame(animationRef.current[deviceId]);
        }

        const startTime = performance.now();
        lastUpdateRef.current[deviceId] = startTime;

        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const lat = startPos[0] + (newPos[0] - startPos[0]) * progress;
          const lng = startPos[1] + (newPos[1] - startPos[1]) * progress;

          vehiclePositionsRef.current[deviceId] = [lat, lng];
          if ((isInViewport || isSelected)) {
            markerRef.current[deviceId]?.setLatLng([lat, lng]);
          }
          if (isSelected) {
            const center = mapRef.getCenter();
            const followCenter = getFollowCenterLatLng(
              mapRef,
              [lat, lng],
              selectedVehicleFollowOffsetY
            );
            if (
              followCenter &&
              (
                Math.abs(center.lat - followCenter.lat) > SELECTED_FOLLOW_EPSILON ||
                Math.abs(center.lng - followCenter.lng) > SELECTED_FOLLOW_EPSILON
              )
            ) {
              mapRef.setView(followCenter, mapRef.getZoom(), { animate: false });
            }
          }

          if (progress < 1) {
            animationRef.current[deviceId] = requestAnimationFrame(animate);
          } else {
            vehiclePositionsRef.current[deviceId] = newPos;
          }
        };

        animationRef.current[deviceId] = requestAnimationFrame(animate);
      }
    });

    return () => {
      Object.values(animationRef.current).forEach(cancelAnimationFrame);
    };
  }, [
    listVehicleTracking,
    mapRef,
    clusters,
    supercluster,
    vehicleSelected,
    isPageVisible,
    selectedVehicleFollowOffsetY
  ]);

  return null;
};

export default AnimationManager;
